import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import { useEffect } from 'react';

export default function DigitalPage() {
  useEffect(() => {
    // Add Schema.org JSON-LD
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Digital Presence - Exalt Ventures Ltd.",
      "description": "Connecting with our customers through innovative digital platforms and seamless online experiences across our brand portfolio. Collectively we have over 1.1 million followers across all our brands and platforms.",
      "url": `${import.meta.env.VITE_SITE_URL || "https://exaltventures.com"}/digital`
    });
    document.head.appendChild(script);
    
    return () => {
      document.head.removeChild(script);
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Navigation />

      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-5xl md:text-5xl text-3xl font-serif text-[#003264] mb-6 leading-tight">Digital Presence</h1>
            <p className="text-base md:text-base text-sm text-[#003264] leading-relaxed">
              Connecting with our customers through innovative digital platforms and seamless online experiences across our brand portfolio. Collectively we have over 1.1 million followers across all our brands and platforms.
            </p>
          </div>
        </div>
      </section>

      {/* Overview */}
      <section className="py-16 md:py-16 py-10 bg-[#FAFAFA]">
        <div className="max-w-6xl mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            <div className="bg-white p-12 md:p-12 p-6">
              <h2 className="text-3xl md:text-3xl text-2xl font-serif text-[#003264] mb-6">Digital Strategy</h2>
              <p className="text-sm text-[#003264] leading-relaxed mb-10 md:mb-10 mb-6">
                In today's connected world, our digital presence complements our physical retail operations by providing customers with additional touchpoints for brand engagement, product discovery, and customer service. We maintain active digital platforms for each brand across our markets, ensuring consistent communication and accessibility.
              </p>
              <div className="grid md:grid-cols-2 gap-10 md:gap-10 gap-6">
                <div>
                  <h3 className="text-xl md:text-xl text-lg font-serif text-[#003264] mb-5 md:mb-5 mb-3">Platform Objectives</h3>
                  <div className="space-y-3 text-sm text-[#003264]">
                    <p className="flex items-start">
                      <span className="w-1.5 h-1.5 bg-[#64C8FA] rounded-full mr-2 mt-1.5"></span>
                      <span>Brand awareness and visibility</span>
                    </p>
                    <p className="flex items-start">
                      <span className="w-1.5 h-1.5 bg-[#64C8FA] rounded-full mr-2 mt-1.5"></span>
                      <span>Customer engagement and community building</span>
                    </p>
                    <p className="flex items-start">
                      <span className="w-1.5 h-1.5 bg-[#64C8FA] rounded-full mr-2 mt-1.5"></span>
                      <span>Product showcasing and seasonal campaigns</span>
                    </p>
                    <p className="flex items-start">
                      <span className="w-1.5 h-1.5 bg-[#64C8FA] rounded-full mr-2 mt-1.5"></span>
                      <span>Store location and contact information</span>
                    </p>
                  </div>
                </div>
                <div>
                  <h3 className="text-xl md:text-xl text-lg font-serif text-[#003264] mb-5 md:mb-5 mb-3">Content Focus</h3>
                  <div className="space-y-3 text-sm text-[#003264]">
                    <p className="flex items-start">
                      <span className="w-1.5 h-1.5 bg-[#64C8FA] rounded-full mr-2 mt-1.5"></span>
                      <span>New collection launches and arrivals</span>
                    </p>
                    <p className="flex items-start">
                      <span className="w-1.5 h-1.5 bg-[#64C8FA] rounded-full mr-2 mt-1.5"></span>
                      <span>Style inspiration and fashion trends</span>
                    </p>
                    <p className="flex items-start">
                      <span className="w-1.5 h-1.5 bg-[#64C8FA] rounded-full mr-2 mt-1.5"></span>
                      <span>Store events and special promotions</span>
                    </p>
                    <p className="flex items-start">
                      <span className="w-1.5 h-1.5 bg-[#64C8FA] rounded-full mr-2 mt-1.5"></span>
                      <span>Customer testimonials and experiences</span>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Brand Platforms */}
      <section className="py-16 md:py-16 py-10 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl md:text-3xl text-2xl font-serif text-[#003264] text-center mb-12 md:mb-12 mb-8">Regional Digital Platforms</h2>
            
            <div className="space-y-6 md:space-y-6 space-y-4">
              <div className="bg-[#FAFAFA] p-10 md:p-10 p-6 hover:shadow-lg transition-shadow duration-300">
                <div className="flex items-start gap-5 md:gap-5 gap-3">
                  <div className="w-12 h-12 md:w-12 md:h-12 w-10 h-10 flex items-center justify-center flex-shrink-0">
                    <i className="ri-shopping-bag-3-line text-3xl md:text-3xl text-2xl text-[#64C8FA]"></i>
                  </div>
                  <div>
                    <h3 className="text-xl md:text-xl text-lg font-serif text-[#003264] mb-3 md:mb-3 mb-2">Dune London Libya</h3>
                    <p className="text-sm text-[#003264] leading-relaxed mb-3">
                      Digital platform serving Libyan customers with updates on the latest footwear collections, accessories, and store information. Features seasonal campaigns and style guides tailored to local preferences.
                    </p>
                    <div className="flex gap-3">
                      <a 
                        href="https://www.instagram.com/dune_london_libya?utm_source=ig_web_button_share_sheet&igsh=ZDNlZDc0MzIxNw==" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="w-8 h-8 flex items-center justify-center bg-white hover:bg-[#64C8FA] transition-colors duration-300 cursor-pointer"
                      >
                        <i className="ri-instagram-line text-xl text-[#003264]"></i>
                      </a>
                      <a 
                        href="https://www.facebook.com/share/1BX9NzZCxe/?mibextid=LQQJ4d" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="w-8 h-8 flex items-center justify-center bg-white hover:bg-[#64C8FA] transition-colors duration-300 cursor-pointer"
                      >
                        <i className="ri-facebook-fill text-xl text-[#003264]"></i>
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-[#FAFAFA] p-10 md:p-10 p-6 hover:shadow-lg transition-shadow duration-300">
                <div className="flex items-start gap-5 md:gap-5 gap-3">
                  <div className="w-12 h-12 md:w-12 md:h-12 w-10 h-10 flex items-center justify-center flex-shrink-0">
                    <i className="ri-shopping-bag-3-line text-3xl md:text-3xl text-2xl text-[#64C8FA]"></i>
                  </div>
                  <div>
                    <h3 className="text-xl md:text-xl text-lg font-serif text-[#003264] mb-3 md:mb-3 mb-2">Dune London Algeria</h3>
                    <p className="text-sm text-[#003264] leading-relaxed mb-3">
                      Dedicated platform for Algerian market showcasing premium footwear and accessories. Provides store locations, new arrivals, and fashion inspiration for the Algerian audience.
                    </p>
                    <div className="flex gap-3">
                      <a 
                        href="https://www.instagram.com/dune_london_algeria?utm_source=ig_web_button_share_sheet&igsh=ZDNlZDc0MzIxNw==" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="w-8 h-8 flex items-center justify-center bg-white hover:bg-[#64C8FA] transition-colors duration-300 cursor-pointer"
                      >
                        <i className="ri-instagram-line text-xl text-[#003264]"></i>
                      </a>
                      <a 
                        href="https://www.facebook.com/profile.php?id=100064344145541" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="w-8 h-8 flex items-center justify-center bg-white hover:bg-[#64C8FA] transition-colors duration-300 cursor-pointer"
                      >
                        <i className="ri-facebook-fill text-xl text-[#003264]"></i>
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-[#FAFAFA] p-10 md:p-10 p-6 hover:shadow-lg transition-shadow duration-300">
                <div className="flex items-start gap-5 md:gap-5 gap-3">
                  <div className="w-12 h-12 md:w-12 md:h-12 w-10 h-10 flex items-center justify-center flex-shrink-0">
                    <i className="ri-shopping-bag-3-line text-3xl md:text-3xl text-2xl text-[#64C8FA]"></i>
                  </div>
                  <div>
                    <h3 className="text-xl md:text-xl text-lg font-serif text-[#003264] mb-3 md:mb-3 mb-2">Dune London Malta</h3>
                    <p className="text-sm text-[#003264] leading-relaxed mb-3">
                      European-focused platform serving both local residents and international visitors. Features comprehensive product catalogs, store finder, and exclusive Malta collections.
                    </p>
                    <div className="flex gap-3">
                      <a 
                        href="https://www.instagram.com/dune_london_malta?utm_source=ig_web_button_share_sheet&igsh=ZDNlZDc0MzIxNw==" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="w-8 h-8 flex items-center justify-center bg-white hover:bg-[#64C8FA] transition-colors duration-300 cursor-pointer"
                      >
                        <i className="ri-instagram-line text-xl text-[#003264]"></i>
                      </a>
                      <a 
                        href="https://www.facebook.com/dunelondonmalta" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="w-8 h-8 flex items-center justify-center bg-white hover:bg-[#64C8FA] transition-colors duration-300 cursor-pointer"
                      >
                        <i className="ri-facebook-fill text-xl text-[#003264]"></i>
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-[#FAFAFA] p-10 md:p-10 p-6 hover:shadow-lg transition-shadow duration-300">
                <div className="flex items-start gap-5 md:gap-5 gap-3">
                  <div className="w-12 h-12 md:w-12 md:h-12 w-10 h-10 flex items-center justify-center flex-shrink-0">
                    <i className="ri-t-shirt-line text-3xl md:text-3xl text-2xl text-[#64C8FA]"></i>
                  </div>
                  <div>
                    <h3 className="text-xl md:text-xl text-lg font-serif text-[#003264] mb-3 md:mb-3 mb-2">Monsoon Accessorize Libya</h3>
                    <p className="text-sm text-[#003264] leading-relaxed mb-3">
                      Combined platform for Monsoon Children and Accessorize brands in Libya. Showcases children's fashion collections and women's accessories with regular updates on new arrivals and seasonal trends.
                    </p>
                    <div className="flex gap-3">
                      <a 
                        href="https://www.instagram.com/monsoon__accessorize_libya/" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="w-8 h-8 flex items-center justify-center bg-white hover:bg-[#64C8FA] transition-colors duration-300 cursor-pointer"
                      >
                        <i className="ri-instagram-line text-xl text-[#003264]"></i>
                      </a>
                      <a 
                        href="https://www.facebook.com/monsoonaccessorizelibya" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="w-8 h-8 flex items-center justify-center bg-white hover:bg-[#64C8FA] transition-colors duration-300 cursor-pointer"
                      >
                        <i className="ri-facebook-fill text-xl text-[#003264]"></i>
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-[#FAFAFA] p-10 md:p-10 p-6 hover:shadow-lg transition-shadow duration-300">
                <div className="flex items-start gap-5 md:gap-5 gap-3">
                  <div className="w-12 h-12 md:w-12 md:h-12 w-10 h-10 flex items-center justify-center flex-shrink-0">
                    <i className="ri-t-shirt-line text-3xl md:text-3xl text-2xl text-[#64C8FA]"></i>
                  </div>
                  <div>
                    <h3 className="text-xl md:text-xl text-lg font-serif text-[#003264] mb-3 md:mb-3 mb-2">Monsoon Accessorize Algeria</h3>
                    <p className="text-sm text-[#003264] leading-relaxed mb-3">
                      Algerian platform featuring both Monsoon Children and Accessorize collections. Provides style inspiration, store information, and customer engagement through social media channels.
                    </p>
                    <div className="flex gap-3">
                      <a 
                        href="https://www.instagram.com/monsoon_algeria/" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="w-8 h-8 flex items-center justify-center bg-white hover:bg-[#64C8FA] transition-colors duration-300 cursor-pointer"
                      >
                        <i className="ri-instagram-line text-xl text-[#003264]"></i>
                      </a>
                      <a 
                        href="https://www.facebook.com/accessorizemonsoonchildrenalgerie" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="w-8 h-8 flex items-center justify-center bg-white hover:bg-[#64C8FA] transition-colors duration-300 cursor-pointer"
                      >
                        <i className="ri-facebook-fill text-xl text-[#003264]"></i>
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Digital Engagement */}
      <section className="py-16 md:py-16 py-10 bg-[#FAFAFA]">
        <div className="max-w-6xl mx-auto px-6">
          <div className="max-w-5xl mx-auto text-center">
            <h2 className="text-3xl md:text-3xl text-2xl font-serif text-[#003264] mb-10 md:mb-10 mb-6">Customer Engagement</h2>
            <p className="text-sm text-[#003264] leading-relaxed mb-12 md:mb-12 mb-8">
              Our digital platforms serve as vital communication channels, enabling us to maintain ongoing relationships with customers, share brand stories, and provide exceptional service beyond the physical store environment.
            </p>
            <div className="grid md:grid-cols-3 gap-10 md:gap-10 gap-6">
              <div>
                <div className="w-12 h-12 flex items-center justify-center mx-auto mb-5 md:mb-5 mb-3">
                  <i className="ri-chat-smile-3-line text-3xl md:text-3xl text-2xl text-[#64C8FA]"></i>
                </div>
                <h3 className="text-base font-serif text-[#003264] mb-2">Responsive Communication</h3>
                <p className="text-sm text-[#003264]">Timely responses to customer inquiries and feedback across all platforms.</p>
              </div>
              <div>
                <div className="w-12 h-12 flex items-center justify-center mx-auto mb-5 md:mb-5 mb-3">
                  <i className="ri-image-line text-3xl md:text-3xl text-2xl text-[#64C8FA]"></i>
                </div>
                <h3 className="text-base font-serif text-[#003264] mb-2">Visual Storytelling</h3>
                <p className="text-sm text-[#003264]">High-quality imagery and content that reflects brand values and aesthetics.</p>
              </div>
              <div>
                <div className="w-12 h-12 flex items-center justify-center mx-auto mb-5 md:mb-5 mb-3">
                  <i className="ri-community-line text-3xl md:text-3xl text-2xl text-[#64C8FA]"></i>
                </div>
                <h3 className="text-base font-serif text-[#003264] mb-2">Community Building</h3>
                <p className="text-sm text-[#003264]">Creating engaged communities around our brands and shared fashion interests.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
